/**
 * MER-493: pure merge/filter/CSV logic for the /admin/audit viewer.
 *
 * Two append-only sources feed one timeline, per the ticket ("so
 * impersonation shows in the same timeline"):
 *   - admin_audit_log (0042): one row per admin_request_action() call
 *     (customer CRUD, tier changes, etc).
 *   - impersonation_event (0016): one row per "View as" start/stop.
 * Both are staff-read-only append-only tables with no client write
 * path (the RPCs are the only writers), so every row here is a real
 * staff action, never a client-fabricated one.
 */

/** admin_audit_log row -> the viewer's unified AuditEntry shape. */
export function normalizeAuditLogEntry(row) {
  return {
    id: row.id,
    occurredAt: row.occurred_at,
    source: "admin_action",
    actor: row.actor_email ?? row.actor_user_id ?? null,
    actionType: row.action_type,
    customerSlug: row.customer_slug ?? null,
    detail: row.payload && typeof row.payload === "object" ? row.payload : {},
  };
}

/**
 * impersonation_event row -> the viewer's unified AuditEntry shape.
 * `tenantSlugById` resolves target_tenant_id to a customer slug (the
 * table itself only carries the uuid); pass an empty Map to fall back
 * to the raw uuid.
 */
export function normalizeImpersonationEntry(row, tenantSlugById) {
  const slugMap = tenantSlugById instanceof Map ? tenantSlugById : new Map();
  return {
    id: row.id,
    occurredAt: row.occurred_at,
    source: "impersonation",
    actor: row.staff_user_id ?? null,
    actionType: row.event_type === "start" ? "impersonation_start" : "impersonation_stop",
    customerSlug: slugMap.get(row.target_tenant_id) ?? row.target_tenant_id ?? null,
    detail: {},
  };
}

/** Merge + sort both sources newest-first. */
export function mergeAuditEntries(adminEntries, impersonationEntries) {
  const all = [...(adminEntries ?? []), ...(impersonationEntries ?? [])];
  return all.sort((a, b) => {
    const ta = Date.parse(a.occurredAt ?? "");
    const tb = Date.parse(b.occurredAt ?? "");
    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
  });
}

function actorMatches(actor, needle) {
  if (typeof actor !== "string" || typeof needle !== "string" || needle.length === 0) {
    return needle == null || needle.length === 0;
  }
  return actor.toLowerCase().includes(needle.toLowerCase());
}

/**
 * Filters: actor (substring match, case-insensitive), actionType
 * (exact), customerSlug (exact), since/until (ISO date strings,
 * inclusive, compared as date-only prefixes so a UI date picker's
 * "yyyy-mm-dd" works without timezone gymnastics).
 */
export function filterAuditEntries(entries, filters = {}) {
  const { actor, actionType, customerSlug, since, until } = filters;
  return entries.filter((e) => {
    if (actor && !actorMatches(e.actor, actor)) return false;
    if (actionType && e.actionType !== actionType) return false;
    if (customerSlug && e.customerSlug !== customerSlug) return false;
    if (since && (typeof e.occurredAt !== "string" || e.occurredAt < since)) return false;
    if (until && (typeof e.occurredAt !== "string" || e.occurredAt.slice(0, 10) > until)) {
      return false;
    }
    return true;
  });
}

function csvEscape(value) {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Serialize entries to a CSV string (header row + one row per entry). */
export function toCsv(entries) {
  const header = ["occurred_at", "source", "actor", "action_type", "customer_slug", "detail"];
  const lines = [header.map(csvEscape).join(",")];
  for (const e of entries) {
    const detail = JSON.stringify(e.detail ?? {});
    lines.push(
      [e.occurredAt, e.source, e.actor ?? "", e.actionType, e.customerSlug ?? "", detail]
        .map(csvEscape)
        .join(","),
    );
  }
  return lines.join("\n");
}
